import axios from 'axios';
import { buildRedemptionURL } from './redemption.utils';

import {
    TypeRedemptionResponseContext,
    TypeRedemptionStatusCode,
    IRedeemCodeData
} from 'types/redemption';
import { IAdditionalDetailDefinition } from 'types/additional-info';

import {
    REDEMPTION_STATUS,
    REDEMPTION_RESPONSE_CODES,
    REDEMPTION_RESPONSE_CONTEXTS
} from './redemption.constants';
import { createRedeemRequestHeaders } from 'constants/request.constants';
import {
    redemptionAdditionalFieldsAdapter,
    redemptionPlatformsAdapter,
    redemptionPYCActionsAdapter, hasSelectedItem
} from './redemption-fields.adapter';
import { RedemptionStatus } from 'widgets/redeem/modules/redemption/redemption.reducer';

export interface IRedeemConfigParams {
    readonly applicationId: string;
    readonly ticket: string;
    readonly sessionId: string;
}

interface IRedeemApiCommonOptions {
    readonly redeemConfigParams: IRedeemConfigParams;
    readonly envName: string;
}

export interface IFetchAvailablePlatformsOptions extends IRedeemApiCommonOptions {
    readonly code: string;
}

export interface IRedeemGiftOptions extends IRedeemApiCommonOptions {
    readonly redeemData: IRedeemCodeData;
}

export interface IRedeemApiOptions extends IRedeemApiCommonOptions {
    readonly redeemData: {
        code: string | null,
        device?: string,
        details?: {
            key: string,
            value: string
        }[]
    };
}

class RedeemService {
    public submitCode = async (options: IFetchAvailablePlatformsOptions) => {
        return this.callRedeemAPI({
            ...options,
            redeemData: {
                code: options.code
            }
        });
    };

    public redeemGift = async (options: IRedeemGiftOptions) => {
        return this.callRedeemAPI({
            ...options,
            redeemData: {
                code: options.redeemData.code,
                device: options.redeemData.platform!.toUpperCase(),
                details: options.redeemData.details
            }
        });
    };

    private callRedeemAPI = async (options: IRedeemApiOptions) => {
        const endPointToCall = buildRedemptionURL(options.envName);

        return axios
            .post(
                endPointToCall,
                {
                    ...options.redeemData
                },
                { headers: createRedeemRequestHeaders(options.redeemConfigParams) }
            )
            .then(this.handleResponse)
            .catch(error => {
                return this.handleResponse(error.response);
            });
    };

    // @ts-ignore
    public handleResponse = (response): RedemptionStatus => {
        const additionalDetailsDefinition: IAdditionalDetailDefinition[] = response?.data?.moreInfo?.additionalDetailsDefinition ?? [];
        const statusCode: TypeRedemptionStatusCode = parseInt(response?.data?.statusCode, 10);
        const context: TypeRedemptionResponseContext = response?.data.context;
        const unlockedContent = response?.data?.moreInfo?.unlockedContent;

        /*
         * As specific errors has context we do not require to break the switch flow, in case if 'context' property is specified.
         * It just returns the first returned value.
         * In case if any context doesn't match, the default case will take its place.
         * */
        switch (statusCode) {
        case REDEMPTION_RESPONSE_CODES.SUCCESS:
            return {
                status: REDEMPTION_STATUS.SUCCESS,
                unlockedContent: unlockedContent
            };
        case REDEMPTION_RESPONSE_CODES.ADDITIONAL_INFO:
            const hiddenFields = hasSelectedItem(additionalDetailsDefinition)
                ? { availableActions: redemptionPYCActionsAdapter(additionalDetailsDefinition) }
                : { codeFirstPlatforms: redemptionPlatformsAdapter(additionalDetailsDefinition) };
            return {
                status: REDEMPTION_STATUS.ADDITIONAL_INFO,
                additionalDetailsDefinition: redemptionAdditionalFieldsAdapter(additionalDetailsDefinition),
                ...hiddenFields
            };
        case REDEMPTION_RESPONSE_CODES.INVALID_CODE:
            if (context === REDEMPTION_RESPONSE_CONTEXTS.CODE) {
                return {
                    status: REDEMPTION_STATUS.INVALID_CODE
                };
            } else {
                break;
            }
        case REDEMPTION_RESPONSE_CODES.CODE_ALREADY_REDEEMED:
            if (context === REDEMPTION_RESPONSE_CONTEXTS.CODE) {
                return {
                    status: REDEMPTION_STATUS.CODE_ALREADY_REDEEMED
                };
            } else {
                break;
            }
        case REDEMPTION_RESPONSE_CODES.USER_ALREADY_REDEEMED:
            if (context === REDEMPTION_RESPONSE_CONTEXTS.CODE) {
                return {
                    status: REDEMPTION_STATUS.USER_ALREADY_REDEEMED
                };
            } else {
                break;
            }
        case REDEMPTION_RESPONSE_CODES.NOT_ELIGIBLE:
            if (context === REDEMPTION_RESPONSE_CONTEXTS.USER_IP) {
                return {
                    status: REDEMPTION_STATUS.NOT_ELIGIBLE
                };
            } else {
                break;
            }
        case REDEMPTION_RESPONSE_CODES.AGE_RESTRICTION:
            return {
                status: REDEMPTION_STATUS.AGE_RESTRICTION
            };
        case REDEMPTION_RESPONSE_CODES.PROFILE_NOT_LINKED:
        case REDEMPTION_RESPONSE_CODES.PROFILE_NOT_AVAILABLE:
            return {
                status: REDEMPTION_STATUS.PROFILE_NOT_LINKED
            };
        default:
            return {
                status: REDEMPTION_STATUS.GENERIC_ERROR
            };
        }

        return {
            status: REDEMPTION_STATUS.GENERIC_ERROR
        };
    };
}

const redeemService = new RedeemService();

export default redeemService;
